import React, { useCallback, useMemo, useState } from "react";
import styled, { css } from "styled-components";

import { Body1, Body2, Heading5 } from "--root/components/typography";
import { Strings } from "--root/strings/en";

import { useTypedStore } from "--client/common/apis/hooks/useTypedStore";
import { TrustedExternalLinkHostnames } from "--client/common/TypedStore";

import { Button } from "--floponents-web/atoms/Button";
import { Form } from "--floponents-web/atoms/Form";
import { SurfaceBody, SurfaceHeader } from "--floponents-web/atoms/Surface";
import { Switch } from "--floponents-web/atoms/Switch";
import { DialogCloseButton } from "--floponents-web/molecules/DialogCloseButton/DialogCloseButton";
import { textNodeFromPoint } from "../../../common/utils/utils";
import { navigateToExternalUrl } from "../../../common/externalNavigation";


const StyledTextExcerpt = styled.div<{ isExpanded: boolean }>`
    background-color: ${props => props.theme.palette.translucentSurface};
    border-radius: ${props => props.theme.radius.small};
    overflow-wrap: break-word;
    padding: ${props => props.theme.spacing(1)};

    ${props => !props.isExpanded && css`
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    `}
`;

interface TextExcerptProps {
    className?: string;
}
const TextExcerpt = (props: React.PropsWithChildren<TextExcerptProps>) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const handleToggleClick = useCallback((ev: React.MouseEvent) => {
        // Don't trigger unexpand when clicking on text (helps with text selection)
        if (isExpanded && textNodeFromPoint(ev.clientX, ev.clientY)) return;
        setIsExpanded(existingIsExpanded => !existingIsExpanded);
    }, [isExpanded]);

    return (
        <StyledTextExcerpt
            isExpanded={isExpanded}
            className={props.className}
            onClick={handleToggleClick}
        >
            {props.children}
        </StyledTextExcerpt>
    );
};

const StyledExternalLinkDialog = styled.div`
    display: contents;
`;
const ExternalLinkDialogHref = styled(TextExcerpt)`
    ${props => props.theme.typography.body2};
    color: ${props => props.theme.palette.text.disabled};;
`;
const ExternalLinkDialogHost = styled.span`
    color: ${props => props.theme.palette.text.primary};
    font-weight: ${props => props.theme.typography.bold};
`;
const ExternalLinkDialogActionsGutter = styled(Form.ActionsGutter)`
    flex-wrap: wrap;
`;
const ExternalLinkDialogTrustSwitch = styled(Switch)`
    margin-right: auto;
`;
const ExternalLinkDialogTrustSwitchLabel = styled(Body2)`
    margin-left: ${props => props.theme.spacing(1)};
`;
const ExternalLinkDialogButtons = styled.div`
    display: flex;
    gap: inherit;
`;

export interface ExternalLinkDialogProps {
    url: string;
    onClose?: () => void;
}
export const ExternalLinkDialog = (props: ExternalLinkDialogProps) => {
    const { onClose } = props;

    const url = useMemo(() => {
        try { return new URL(props.url); } catch { /* empty */ }
        return undefined;
    }, [props.url]);

    const [isSiteTrusted, setIsSiteTrusted] = useState(false);
    const handleTrustedChecked = useCallback((ev: React.ChangeEvent<HTMLInputElement>) => {
        setIsSiteTrusted(ev.target.checked);
    }, []);

    const trustedHostnames = useTypedStore(TrustedExternalLinkHostnames);

    const handleVisitSite = useCallback(() => {
        // Add to trusted hostnames if applicable
        if (isSiteTrusted && url && !trustedHostnames?.includes(url.hostname)) {
            TrustedExternalLinkHostnames.set(trustedHostnames
                ? [...trustedHostnames, url.hostname]
                : [url.hostname]);
        }
        if (url)
            navigateToExternalUrl(url?.href);
        onClose?.();
    }, [isSiteTrusted, onClose, trustedHostnames, url]);
    return (
        <StyledExternalLinkDialog>
            <SurfaceHeader>
                <Heading5>{Strings.externalLink.title}</Heading5>
                <DialogCloseButton onClick={onClose} />
            </SurfaceHeader>
            <SurfaceBody>
                <Form.BasicForm>
                    <Body1>{Strings.externalLink.message}</Body1>
                    <ExternalLinkDialogHref>
                        {url
                            ? <>
                                {url.protocol}{"//"}
                                <ExternalLinkDialogHost>{url.hostname}</ExternalLinkDialogHost>
                                {url.pathname}{url.search}{url.hash}
                            </>
                            : props.url}
                    </ExternalLinkDialogHref>
                    <ExternalLinkDialogActionsGutter>
                        {url && (
                            <ExternalLinkDialogTrustSwitch
                                collapse
                                label={
                                    <ExternalLinkDialogTrustSwitchLabel>
                                        {Strings.externalLink.trustSite}
                                    </ExternalLinkDialogTrustSwitchLabel>
                                }
                                checked={isSiteTrusted}
                                onChange={handleTrustedChecked}
                            />
                        )}
                        <ExternalLinkDialogButtons>
                            <Button variant="text" onClick={onClose}>
                                {Strings.cancel}
                            </Button>
                            <Button onClick={handleVisitSite} role="link">
                                {Strings.externalLink.visitSite}
                            </Button>
                        </ExternalLinkDialogButtons>
                    </ExternalLinkDialogActionsGutter>
                </Form.BasicForm>
            </SurfaceBody>
        </StyledExternalLinkDialog>
    );
};
